import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Activity, RefreshCw, CheckCircle2, XCircle, AlertCircle, Clock } from 'lucide-react';
import { api } from '../lib/api';

interface ServiceStatus {
  name: string;
  port: number;
  liveness: 'ok' | 'degraded' | 'error' | 'pending';
  readiness: 'ready' | 'unavailable' | 'error' | 'pending';
  latencyMs: number | null;
  lastChecked: Date | null;
}

const SERVICES: { name: string; port: number }[] = [
  { name: 'API Gateway',      port: 7501 },
  { name: 'Event Gateway',    port: 7502 },
  { name: 'Ledger',           port: 7503 },
  { name: 'Central Bank',     port: 7504 },
  { name: 'World Registry',   port: 7505 },
  { name: 'Exchange',         port: 7506 },
  { name: 'Reward Scaling',   port: 7507 },
  { name: 'Workflow',         port: 7508 },
  { name: 'Analytics',        port: 7509 },
  { name: 'Reporting',        port: 7510 },
  { name: 'Payment',          port: 7511 },
];

interface ApiProbeResult {
  name: string;
  port: number;
  liveness: 'ok' | 'degraded' | 'error';
  readiness: 'ready' | 'unavailable' | 'error';
  latencyMs: number | null;
}

/**
 * Fetches aggregated health status for all services from the api-gateway proxy
 * endpoint. Running probes server-side avoids the CORS issues that arise from
 * the browser directly hitting sibling services on different ports.
 */
async function fetchAllHealth(): Promise<ApiProbeResult[]> {
  const { data } = await api.get<ApiProbeResult[]>('/health/services');
  return data;
}

function StatusPill({ label, color }: { label: string; color: 'green' | 'red' | 'amber' | 'slate' }) {
  const map = {
    green: 'text-emerald-400 bg-emerald-400/10 border-emerald-400/20',
    red:   'text-red-400 bg-red-400/10 border-red-400/25',
    amber: 'text-amber-400 bg-amber-400/10 border-amber-400/20',
    slate: 'text-slate-600 bg-slate-600/10 border-slate-600/20',
  };
  return (
    <span className={`inline-flex items-center text-[10px] font-semibold px-2 py-0.5 rounded-md border ${map[color]}`}>
      {label}
    </span>
  );
}

function ServiceCard({ svc }: { svc: ServiceStatus }) {
  const allOk = svc.liveness === 'ok' && svc.readiness === 'ready';
  const pending = svc.liveness === 'pending';
  const latencyDegraded = svc.liveness === 'degraded';
  const degraded = (svc.liveness === 'ok' && svc.readiness !== 'ready') || latencyDegraded;

  return (
    <motion.div
      className="card p-5 flex flex-col gap-4"
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
    >
      {/* Service name + status indicator */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2.5">
          <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
            pending ? 'bg-slate-600' :
            allOk   ? 'bg-emerald-400 shadow-[0_0_6px_rgba(52,211,153,0.5)]' :
            degraded ? 'bg-amber-400 shadow-[0_0_6px_rgba(251,191,36,0.5)]' :
                       'bg-red-400 shadow-[0_0_6px_rgba(248,113,113,0.5)]'
          }`} />
          <span className="text-sm font-semibold text-slate-200">{svc.name}</span>
        </div>
        <span className="text-[10px] text-slate-700 font-mono">:{svc.port}</span>
      </div>

      {/* Probe results */}
      <div className="flex items-center gap-2 flex-wrap">
        {pending ? (
          <StatusPill label="Checking…" color="slate" />
        ) : (
          <>
            {svc.liveness === 'ok'
              ? <StatusPill label="LIVE" color="green" />
              : svc.liveness === 'degraded'
              ? <StatusPill label="DEGRADED" color="amber" />
              : <StatusPill label="DOWN" color="red" />}
            {svc.readiness === 'ready'
              ? <StatusPill label="READY" color="green" />
              : svc.readiness === 'unavailable'
              ? <StatusPill label="NOT READY" color="amber" />
              : <StatusPill label="NO PROBE" color="slate" />}
          </>
        )}
      </div>

      {/* Icon row */}
      <div className="flex items-center justify-between mt-auto">
        <div className="flex items-center gap-3">
          {!pending && (
            <>
              {svc.liveness === 'ok'
                ? <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                : <XCircle className="w-4 h-4 text-red-400" />}
              {svc.readiness === 'ready'
                ? <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                : svc.readiness === 'unavailable'
                ? <AlertCircle className="w-4 h-4 text-amber-400" />
                : <XCircle className="w-4 h-4 text-red-400/50" />}
            </>
          )}
        </div>
        <span className="text-[10px] font-mono text-slate-700">
          {svc.latencyMs !== null ? `${svc.latencyMs} ms` : '—'}
        </span>
      </div>

      {svc.lastChecked && (
        <div className="flex items-center gap-1 text-[10px] text-slate-700">
          <Clock className="w-3 h-3" />
          {svc.lastChecked.toLocaleTimeString()}
        </div>
      )}
    </motion.div>
  );
}

export function ServiceHealthPage() {
  const [statuses, setStatuses] = useState<ServiceStatus[]>(() =>
    SERVICES.map((s) => ({
      ...s,
      liveness: 'pending',
      readiness: 'pending',
      latencyMs: null,
      lastChecked: null,
    })),
  );
  const [polling, setPolling] = useState(false);

  async function checkAll() {
    setPolling(true);
    try {
      const results = await fetchAllHealth();
      const lastChecked = new Date();
      setStatuses(results.map((r) => ({ ...r, lastChecked })));
    } catch {
      // api-gateway itself is unreachable — mark everything as down
      setStatuses((prev) =>
        prev.map((s) => ({
          ...s,
          liveness: 'error' as const,
          readiness: 'error' as const,
          latencyMs: null,
          lastChecked: new Date(),
        })),
      );
    } finally {
      setPolling(false);
    }
  }

  // Run on mount and every 30 s
  useEffect(() => {
    checkAll();
    const id = setInterval(checkAll, 30_000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const upCount = statuses.filter((s) => s.liveness === 'ok' || s.liveness === 'degraded').length;
  const degradedCount = statuses.filter((s) => s.liveness === 'degraded').length;
  const readyCount = statuses.filter((s) => s.readiness === 'ready').length;

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
            <Activity className="w-4 h-4 text-emerald-400" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">Service Health</h2>
            <p className="text-sm text-slate-600 mt-0.5">Auto-polls every 30 s · probes /health + /ready</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-xs text-slate-600">
            <span className="text-emerald-400 font-mono font-bold">{upCount}</span>
            <span>/{SERVICES.length} live</span>
            {degradedCount > 0 && <span className="text-amber-400 font-mono font-bold ml-2">{degradedCount} degraded</span>}
            <span className="ml-2">· </span>
            <span className="text-emerald-400 font-mono font-bold">{readyCount}</span>
            <span>/{SERVICES.length} ready</span>
          </div>
          <button
            onClick={checkAll}
            disabled={polling}
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs text-slate-400 hover:text-white border border-white/[0.06] hover:border-white/10 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${polling ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Summary bar */}
      <div className="card p-4 mb-6 flex items-center gap-6">
        <div className="text-center">
          <div className="text-2xl font-bold font-mono text-emerald-400">{upCount}/{SERVICES.length}</div>
          <div className="text-[10px] text-slate-600 tracking-widest uppercase mt-0.5">Live</div>
        </div>
        <div className="w-px h-10 bg-white/[0.06]" />
        <div className="text-center">
          <div className="text-2xl font-bold font-mono text-emerald-400">{readyCount}/{SERVICES.length}</div>
          <div className="text-[10px] text-slate-600 tracking-widest uppercase mt-0.5">Ready</div>
        </div>
        <div className="w-px h-10 bg-white/[0.06]" />
        {/* Visual health bar */}
        <div className="flex-1">
          <div className="flex gap-1 h-2">
            {statuses.map((s) => (
              <div
                key={s.port}
                className={`flex-1 rounded-sm ${
                  s.liveness === 'pending' ? 'bg-slate-800' :
                  s.liveness === 'degraded' ? 'bg-amber-500' :
                  s.liveness === 'ok' && s.readiness === 'ready' ? 'bg-emerald-500' :
                  s.liveness === 'ok' ? 'bg-amber-500' : 'bg-red-500'
                }`}
                title={s.name}
              />
            ))}
          </div>
          <div className="text-[10px] text-slate-700 mt-1.5">Service health overview</div>
        </div>
      </div>

      {/* Grid of service cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {statuses.map((svc) => (
          <ServiceCard key={svc.port} svc={svc} />
        ))}
      </div>
    </div>
  );
}
